import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Progress, Table, Tag, Select, Button, Spin, Empty, Space } from 'antd';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { PageHeader, Card } from '@/components/common/PageHeader';
import { dashboardService, type DepartmentDashboard } from '@/services/dashboardService';
import { taskService } from '@/services/taskService';
import { createCategoryService } from '@/services/categoryService';
import { orgService, type DepartmentGroup } from '@/services/rolesService';
import { useAuthStore } from '@/store/authStore';
import { EisenhowerMatrix } from '@/components/dashboard/EisenhowerMatrix';

const deptService = createCategoryService<any>('departments');

const CATEGORY_LABELS: Record<string, string> = {
  ADMIN: 'Hành chính',
  TECHNICAL: 'Kỹ thuật',
  MEDICAL: 'Y khoa',
  OTHER: 'Khác',
};

const COLORS = ['#6699CC', '#41b6a7', '#2a3547', '#faad14', '#f5222d', '#722ed1'];

export function DepartmentDashboardPage() {
  const navigate = useNavigate();
  const { user, hasAnyRole } = useAuthStore();
  const [selectedDeptId, setSelectedDeptId] = useState<string | undefined>(user?.department?.id);
  // V1.0.33: Filter theo nhóm (lọc dropdown department theo nhóm đã chọn)
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>();

  const canSwitchDept = hasAnyRole(['SYS_ADMIN', 'BOD']);

  const { data: departments = [] } = useQuery({
    queryKey: ['departments-list'],
    queryFn: () => deptService.list(),
    enabled: canSwitchDept,
  });

  // V1.0.33: Load nhóm khoa phòng
  const { data: groups = [] } = useQuery<DepartmentGroup[]>({
    queryKey: ['dept-groups'],
    queryFn: () => orgService.listGroups(),
    enabled: canSwitchDept,
  });

  // V1.0.33: Filter departments dropdown theo nhóm đã chọn
  const filteredDepartments = useMemo(() => {
    if (!selectedGroupId) return departments;
    return (departments as any[]).filter(d => d.groupId === selectedGroupId);
  }, [departments, selectedGroupId]);

  // V1.0.36: Danh sách dept ID cần load task — phục vụ ma trận Eisenhower
  // - Nếu chọn cụ thể 1 khoa → chỉ load khoa đó
  // - Nếu chỉ chọn nhóm (chưa chọn khoa) → load tất cả khoa trong nhóm
  // - Nếu không chọn gì → load của user hiện tại (selectedDeptId default)
  const targetDeptIds = useMemo(() => {
    if (selectedDeptId) return [selectedDeptId];
    if (selectedGroupId) {
      return (filteredDepartments as any[]).map(d => d.id);
    }
    return user?.department?.id ? [user.department.id] : [];
  }, [selectedDeptId, selectedGroupId, filteredDepartments, user]);

  // Khi đổi nhóm → tự động clear selectedDept nếu không thuộc nhóm mới
  useEffect(() => {
    if (selectedGroupId && selectedDeptId) {
      const inGroup = (departments as any[]).find(d => d.id === selectedDeptId);
      if (inGroup && inGroup.groupId !== selectedGroupId) {
        setSelectedDeptId(undefined);
      }
    }
  }, [selectedGroupId, departments]);

  // V1.0.36: Mode aggregate theo nhóm — khi user chọn group nhưng không chọn dept cụ thể
  const isGroupMode = !selectedDeptId && !!selectedGroupId;

  // Single dept mode: load như cũ
  const { data: singleData, isLoading: singleLoading, error } = useQuery<DepartmentDashboard>({
    queryKey: ['dashboard-department', selectedDeptId],
    queryFn: () => dashboardService.department(selectedDeptId),
    refetchInterval: 60 * 1000,
    retry: false,
    enabled: !!selectedDeptId && !isGroupMode,
  });

  // V1.0.36: Group mode — load song song dashboard từng khoa trong nhóm rồi merge
  const { data: groupData, isLoading: groupLoading } = useQuery<DepartmentDashboard>({
    queryKey: ['dashboard-group', selectedGroupId, targetDeptIds.sort().join(',')],
    queryFn: async () => {
      if (targetDeptIds.length === 0) {
        return emptyDashboard(selectedGroupId, groups);
      }
      const results = await Promise.all(
        targetDeptIds.map(did =>
          dashboardService.department(did).catch(() => null)
        )
      );
      const valid = results.filter((r): r is DepartmentDashboard => r != null);
      return mergeDashboards(valid, selectedGroupId, groups, targetDeptIds.length);
    },
    enabled: isGroupMode && targetDeptIds.length > 0,
    refetchInterval: 60 * 1000,
  });

  // Chọn data hiển thị tùy mode
  const data = isGroupMode ? groupData : singleData;
  const isLoading = isGroupMode ? groupLoading : singleLoading;

  // V1.0.36: Lấy task cho ma trận — gọi parallel cho nhiều dept khi filter group
  const { data: deptTasks, isLoading: deptTasksLoading } = useQuery({
    queryKey: ['tasks-for-matrix', targetDeptIds.sort().join(',')],
    queryFn: async () => {
      if (targetDeptIds.length === 0) return { items: [] };
      // Load song song task của từng dept rồi merge
      const results = await Promise.all(
        targetDeptIds.map(did =>
          taskService.list({ departmentId: did, pageSize: 500 } as any)
            .catch(() => ({ items: [] }))
        )
      );
      // Dedupe theo task.id (tránh task cross-dept bị đếm 2 lần)
      const seen = new Set<string>();
      const merged: any[] = [];
      for (const r of results) {
        for (const t of (r.items ?? [])) {
          if (!seen.has(t.id)) { seen.add(t.id); merged.push(t); }
        }
      }
      return { items: merged };
    },
    enabled: targetDeptIds.length > 0,
  });

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h3 style={{ color: '#f5222d' }}>Lỗi tải dashboard khoa</h3>
        <p style={{ color: '#7d8a98' }}>{(error as any)?.response?.data?.message ?? (error as any)?.message ?? 'Unknown'}</p>
      </div>
    );
  }

  if (isLoading || !data) {
    return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;
  }

  const distributionData = data.distribution.map((d, i) => ({
    name: CATEGORY_LABELS[d.category] ?? d.category,
    value: d.count,
    color: COLORS[i % COLORS.length],
  }));

  const totalTasks = data.openTasks + data.completedTasks;
  const completionRate = totalTasks > 0
    ? Math.round(data.completedTasks / totalTasks * 100) : 0;

  return (
    <>
      <PageHeader
        breadcrumb={isGroupMode ? "NHÓM KHOA PHÒNG" : "KHOA / PHÒNG"}
        title={data.departmentName}
        subtitle={isGroupMode
          ? `Tổng hợp ${targetDeptIds.length} khoa · ${data.staffCount} nhân sự · ${data.openTasks} task đang mở`
          : `${data.staffCount} nhân sự · ${data.openTasks} task đang mở`}
        extra={canSwitchDept ? (
          <Space>
            {/* V1.0.33: Filter theo nhóm khoa phòng */}
            <Select
              allowClear
              placeholder="Tất cả nhóm"
              style={{ width: 220 }}
              value={selectedGroupId}
              onChange={setSelectedGroupId}
              showSearch
              optionFilterProp="label"
              options={groups.filter(g => g.isActive).map(g => ({
                value: g.id,
                label: g.name,
              }))}
            />
            <Select
              allowClear
              placeholder={selectedGroupId ? '(Để trống = xem cả nhóm)' : 'Chọn khoa/phòng'}
              style={{ width: 260 }}
              value={selectedDeptId}
              onChange={setSelectedDeptId}
              showSearch
              optionFilterProp="label"
              options={(filteredDepartments as any[]).map(d => ({
                value: d.id,
                label: `${d.code} - ${d.name}`,
              }))}
            />
          </Space>
        ) : undefined}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card>
            <div style={{ fontSize: 11, color: '#7d8a98', textTransform: 'uppercase' }}>Đang mở</div>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#6699CC' }}>{data.openTasks}</div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <div style={{ fontSize: 11, color: '#7d8a98', textTransform: 'uppercase' }}>Sắp đến hạn</div>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#faad14' }}>{data.dueSoonTasks}</div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <div style={{ fontSize: 11, color: '#7d8a98', textTransform: 'uppercase' }}>Quá hạn</div>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#f5222d' }}>{data.overdueTasks}</div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <div style={{ fontSize: 11, color: '#7d8a98', textTransform: 'uppercase' }}>Tỷ lệ hoàn thành</div>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#52c41a' }}>{completionRate}%</div>
            <Progress percent={completionRate} showInfo={false} size="small"
              strokeColor="#52c41a" style={{ marginTop: 4 }} />
          </Card>
        </Col>
      </Row>

      {/* V1.0.34 H3: Phân loại task chủ quản / hỗ trợ */}
      {((data.primaryTasks ?? 0) > 0 || (data.supportingTasks ?? 0) > 0) && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={12}>
            <div style={{ borderLeft: '4px solid #1976D2', borderRadius: 4, overflow: 'hidden' }}>
              <Card>
                <div style={{ fontSize: 11, color: '#7d8a98', textTransform: 'uppercase' }}>
                  ⭐ Chủ quản
                </div>
                <div style={{ fontSize: 28, fontWeight: 600, color: '#1976D2' }}>
                  {data.primaryTasks ?? 0}
                </div>
                <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 4 }}>
                  Task khoa là chủ quản chính
                </div>
              </Card>
            </div>
          </Col>
          <Col xs={12} md={12}>
            <div style={{ borderLeft: '4px solid #a3a3a3', borderRadius: 4, overflow: 'hidden' }}>
              <Card>
                <div style={{ fontSize: 11, color: '#7d8a98', textTransform: 'uppercase' }}>
                  🤝 Đang hỗ trợ
                </div>
                <div style={{ fontSize: 28, fontWeight: 600, color: '#5b6875' }}>
                  {data.supportingTasks ?? 0}
                </div>
                <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 4 }}>
                  Task khoa khác chủ quản, có nhân viên khoa đang làm
                </div>
              </Card>
            </div>
          </Col>
        </Row>
      )}

      {/* Ma trận Eisenhower của khoa/phòng — V1.0.36: filter theo group hoặc dept */}
      <div style={{ marginBottom: 16 }}>
        <EisenhowerMatrix
          tasks={deptTasks?.items ?? []}
          loading={deptTasksLoading}
          title={
            selectedDeptId
              ? `📊 Ma trận Eisenhower — ${data.departmentName ?? 'Khoa/phòng'}`
              : selectedGroupId
                ? `📊 Ma trận Eisenhower — Nhóm ${groups.find(g => g.id === selectedGroupId)?.name ?? '...'} (${targetDeptIds.length} khoa)`
                : `📊 Ma trận Eisenhower — Khoa/phòng`
          }
        />
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={10}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              Phân bố task theo loại
            </div>
            {distributionData.length === 0 ? (
              <Empty description="Chưa có dữ liệu" style={{ padding: 40 }} />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={distributionData}
                    cx="50%" cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    dataKey="value"
                    label={(e: any) => `${e.name} (${e.value})`}
                  >
                    {distributionData.map((e, i) => (
                      <Cell key={i} fill={e.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        <Col xs={24} md={14}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              Khối lượng công việc theo nhân sự (Top 20)
            </div>
            {data.workload.length === 0 ? (
              <Empty description="Chưa có dữ liệu" style={{ padding: 40 }} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {data.workload.map((w) => {
                  const total = w.totalOpen || 1;
                  const trackPct = (w.onTrack / total) * 100;
                  const warnPct = (w.warning / total) * 100;
                  const latePct = (w.late / total) * 100;
                  return (
                    <div key={w.userId}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                        <span style={{ fontWeight: 500 }}>{w.fullName}</span>
                        <span style={{ color: '#7d8a98' }}>
                          {w.totalOpen} task
                          {w.late > 0 && <span style={{ color: '#f5222d', marginLeft: 6 }}>· {w.late} trễ</span>}
                          {w.warning > 0 && <span style={{ color: '#faad14', marginLeft: 6 }}>· {w.warning} sắp hạn</span>}
                        </span>
                      </div>
                      <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#eceef1' }}>
                        <div style={{ width: `${trackPct}%`, background: '#52c41a' }} />
                        <div style={{ width: `${warnPct}%`, background: '#faad14' }} />
                        <div style={{ width: `${latePct}%`, background: '#f5222d' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
              <span>Task quá hạn ({data.overdueList.length})</span>
              <Button type="link" size="small" onClick={() => navigate('/tasks?filter=overdue')}>
                Xem tất cả
              </Button>
            </div>
            <Table
              size="small"
              dataSource={data.overdueList}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: <Empty description="Không có task quá hạn" /> }}
              columns={[
                { title: 'Mã', dataIndex: 'code', width: 120, render: (v: string) => <span className="mono" style={{ fontSize: 11 }}>{v}</span> },
                { title: 'Tiêu đề', dataIndex: 'title',
                  render: (v: string, r: any) => <a onClick={() => navigate(`/tasks/${r.id}`)}>{v}</a> },
                // V1.0.34 H3: Badge phân biệt chủ quản / hỗ trợ
                {
                  title: 'Loại', dataIndex: 'isPrimary', width: 110,
                  render: (v: boolean | undefined) => v === false
                    ? <Tag color="default">🤝 Hỗ trợ</Tag>
                    : <Tag color="blue">⭐ Chủ quản</Tag>,
                },
                { title: 'Người thực hiện', dataIndex: 'assigneeName', width: 160 },
                { title: 'Hạn', dataIndex: 'dueDate', width: 100,
                  render: (v: string) => <span className="mono" style={{ fontSize: 11 }}>{v}</span> },
                { title: 'Quá hạn', dataIndex: 'daysOverdue', width: 100,
                  render: (v: number) => <Tag color="error">{v} ngày</Tag> },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
}

// ============================================================
// V1.0.36: Helpers cho mode group — gộp data từ nhiều khoa
// ============================================================

/**
 * Merge nhiều DepartmentDashboard thành 1 — phục vụ view theo nhóm khoa phòng.
 * - Stats: cộng dồn
 * - Distribution: cộng theo category
 * - Workload: gộp tất cả users, sort theo totalOpen desc
 * - OverdueList: gộp, dedupe theo task.id, sort theo daysOverdue desc, lấy top 50
 */
function mergeDashboards(
  list: DepartmentDashboard[],
  groupId: string | undefined,
  groups: DepartmentGroup[],
  totalDeptCount: number
): DepartmentDashboard {
  const group = groups.find(g => g.id === groupId);
  const groupName = group?.name ?? 'Nhóm khoa phòng';

  // Cộng dồn stats
  const sumStats = list.reduce((acc, d) => ({
    openTasks: acc.openTasks + d.openTasks,
    completedTasks: acc.completedTasks + d.completedTasks,
    overdueTasks: acc.overdueTasks + d.overdueTasks,
    dueSoonTasks: acc.dueSoonTasks + d.dueSoonTasks,
    staffCount: acc.staffCount + d.staffCount,
    primaryTasks: (acc.primaryTasks ?? 0) + (d.primaryTasks ?? 0),
    supportingTasks: (acc.supportingTasks ?? 0) + (d.supportingTasks ?? 0),
  }), {
    openTasks: 0, completedTasks: 0, overdueTasks: 0, dueSoonTasks: 0,
    staffCount: 0, primaryTasks: 0, supportingTasks: 0,
  });

  // Gộp distribution theo category
  const distMap = new Map<string, number>();
  for (const d of list) {
    for (const item of d.distribution) {
      distMap.set(item.category, (distMap.get(item.category) ?? 0) + item.count);
    }
  }
  const distribution = Array.from(distMap.entries())
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count);

  // Gộp workload — tất cả users, sort theo totalOpen desc
  const workloadMap = new Map<string, any>();
  for (const d of list) {
    for (const w of d.workload) {
      if (!workloadMap.has(w.userId)) {
        workloadMap.set(w.userId, { ...w });
      } else {
        // Cùng user xuất hiện ở nhiều khoa (cross-dept) → cộng dồn
        const existing = workloadMap.get(w.userId);
        existing.totalOpen += w.totalOpen;
        existing.onTrack += w.onTrack;
        existing.warning += w.warning;
        existing.late += w.late;
      }
    }
  }
  const workload = Array.from(workloadMap.values())
    .sort((a, b) => b.totalOpen - a.totalOpen)
    .slice(0, 50);

  // Gộp overdueList — dedupe theo task.id
  const seenTasks = new Set<string>();
  const overdueList: any[] = [];
  for (const d of list) {
    for (const t of d.overdueList) {
      if (!seenTasks.has(t.id)) {
        seenTasks.add(t.id);
        overdueList.push(t);
      }
    }
  }
  overdueList.sort((a, b) => b.daysOverdue - a.daysOverdue);

  return {
    departmentId: groupId ?? '',
    departmentCode: group?.code ?? '',
    departmentName: `${groupName} (${totalDeptCount} khoa)`,
    ...sumStats,
    distribution,
    workload,
    overdueList: overdueList.slice(0, 50),
  };
}

function emptyDashboard(
  groupId: string | undefined,
  groups: DepartmentGroup[]
): DepartmentDashboard {
  const group = groups.find(g => g.id === groupId);
  return {
    departmentId: groupId ?? '',
    departmentCode: group?.code ?? '',
    departmentName: group?.name ?? 'Nhóm khoa phòng',
    staffCount: 0, openTasks: 0, completedTasks: 0,
    overdueTasks: 0, dueSoonTasks: 0,
    primaryTasks: 0, supportingTasks: 0,
    distribution: [],
    workload: [],
    overdueList: [],
  };
}
